// Plans 2194 / 2200: the Summary and Deep Dive blocks under the six cards on the influenced basis. The adapters map the
// nightly rows (/exec/influence/workshops, /trend, /segments, /tags) onto the shapes the existing tables and charts read.

export const INFLUENCED_BASIS = 'TEC-influenced deals (an SE, technical team member or contact attended before the deal closed). Reach, not revenue.';
// Plan 2208 B1: ONE wording for every place that shows a customer-account / partner line (KPI cards, Deep Dive segment cards,
// Participant Brief tiles, Methodology table). Pending matches are in no total.
export const domainMatchNote = 'Customer-account and partner lines count confirmed domain matches only; matches pending review are in no total (Admin > Domain Matches).';
export const pendingMatchNote = (n) => (n > 0 ? `${domainMatchNote} ${Number(n).toLocaleString()} matches pending review.` : domainMatchNote);
export const pendingDealsText = (p) => (p?.n_deals ? `Pending review (not counted): ${p.n_deals.toLocaleString()} deals` : null);
export const PREVIOUS_BASIS = 'attendee-linked deals (previous definition)';
export const PREVIOUS_BASIS_NOTE = `Basis: ${PREVIOUS_BASIS}. The six cards above use the TEC-influenced definition.`;
export const TAG_TABLE_BASIS = {
  Product: 'Basis: TEC-influenced deals created in the period. Amounts are the deal lines whose SKU tag matches a tag of an attended workshop or event (Product attributed). Reach, not revenue.',
  Pillar: 'Basis: TEC-influenced deals created in the period. Amounts are the lines whose SKU tag an attended workshop carries together with the pillar. A deal can sit under several pillars, so rows do not sum. Reach, not revenue.',
  Competency: 'Basis: TEC-influenced deals created in the period. Amounts are the lines whose SKU tag an attended workshop carries together with the competency. A deal can sit under several competencies, so rows do not sum. Reach, not revenue.',
  Summary: 'Basis: TEC-influenced deals created in the period; Product, Pillar and Competency rows only (other tag categories carry no dollars). Pillar and Competency rows are roll-ups of the same lines and do not add to the Product rows.',
};
// Plan 2213 phase 4: every influenced number opens its deal list (GET /exec/influence/deals) when the influence service is on.
export const DRILL_UNAVAILABLE = 'Deal lists for these rows open when the influence service is on; they are not available in this view.';
export const DRILL_SURFACE_OFF = 'Deal lists are switched off on this page.';
export const drillOn = (status, surface) => !!(status?.influence_service && status?.cutover?.enabled && status?.influence_drill?.includes(surface));
// Plan 2213 phase 3: Tag Analytics audience -> engine segment. External (customers and partners together) is not one segment.
export const TAG_SEGMENT = { all: 'all', internal: 'internal-direct', customer: 'customer', channel: 'partner' };
export const EXTERNAL_SEGMENT_MESSAGE = 'The External audience (customers and partners together) is not computed as one segment. Pick Customer or Partner, or Internal.';
export const OVERLAP_BASIS = 'Basis: TEC-influenced deals created in the period. A deal is counted under every Pillar its credited workshops carry with a matching deal line, so cells and rows do not sum; a diagonal cell equals that Pillar\'s deal count above. Reach, not revenue.';
export const CROSS_BASIS = 'Won revenue is the credited deal lines of TEC-influenced deals, by product SKU. Counts are influenced deals, not people.';

// /exec/influence/overlap topics -> the rows CrossCompetencyCallout reads (count = influenced deals).
export const adaptTopics = (data) => (data?.available ? (data.topics || []).map((t) => ({
  tag_id: t.tag_id, workshop_tag: t.workshop_tag, workshop_tag_category: t.workshop_tag_category, participants: t.n_deals, total_won: t.total_won,
  revenue_by_product: (t.revenue_by_product || []).map((p) => ({ product_tag: p.product_tag, won_amount: p.won_amount, opp_count: p.n_won })),
})).filter((t) => t.total_won > 0) : []);
export const TIER_LINE = 'Pipeline and won: Product attributed lines. Lift, speed and size: all TEC Program influenced deals.';


// Plan 2206 step 1: Participant Brief money tiles from /exec/influence/participant-money, one section per audience.
export const PB_SEGMENT = { internal: 'internal-direct', customer: 'customer', channel: 'partner' };
export const PB_BASIS = 'Money tiles: TEC Program influenced deals (whole deals, all stages) created in the period, one audience per deal; '
  + 'win rate is closed deals only, with n and open share. Reach, not revenue. People tiles are unchanged.';
export const PB_FALLBACK_BASIS = `Money tiles: ${PREVIOUS_BASIS}.`;

export function adaptParticipantMoney(seg) {
  if (!seg) return null;
  return {
    pipeline: seg.pipeline, n_deals: seg.n_deals, won: seg.won, n_won: seg.n_won,
    win_rate: seg.win_rate_pct == null ? null : seg.win_rate_pct / 100,
    win_rate_n: seg.n_closed, open_share: seg.open_share_pct == null ? null : seg.open_share_pct / 100,
    avg_won_deal: seg.avg_won_deal,
  };
}

export function buildBlockQuery({ startDate, endDate, tecProgramTagId, segment, category, tier, limit, eventId }) {
  const p = new URLSearchParams();
  if (startDate && endDate) { p.set('startDate', startDate); p.set('endDate', endDate); }
  if (tecProgramTagId) p.set('tecProgramTagId', String(tecProgramTagId));
  if (segment && segment !== 'all') p.set('segment', segment);
  if (category) p.set('category', category);
  if (tier) p.set('tier', tier);
  if (eventId) p.set('event_id', String(eventId));
  if (limit) p.set('limit', String(limit));
  return p.toString();
}

const frac = (pct) => (pct == null ? null : pct / 100);

export function adaptTrend(data) {
  if (!data?.available || !data.quarters?.length) return null;
  return {
    quarters: data.quarters.map((q) => q.replace('Q', '-Q')),
    pipeline: data.pipeline,
    won: data.won,
    win_rate: (data.win_rate_pct || []).map(frac),
    win_rate_n: data.win_rate_n,
    open_share: (data.open_share_pct || []).map(frac),
  };
}

// Plan 2213: with the influence service on, a view that cannot be answered right now says so in plain words (the API sends the
// sentence in `message`); the previous-definition numbers are never shown in its place.
export const RELOADING_MESSAGE = 'The numbers for this view are reloading. Try again in a minute.';
export const isReloading = (data) => data?.available === false && data?.reason === 'influence_reloading';
export const reloadingText = (data) => data?.message || RELOADING_MESSAGE;
// Model metrics (lift, landmark, speed, size) on a period the nightly job does not compute carry this label (decision D3).
export const EXPLORATORY_LABEL = 'Exploratory: not a standard period';
export const isExploratory = (data) => data?.basis?.standard_period === false;

export const blockReasonText = (reason) => (reason === 'period_not_precomputed'
  ? 'this period is not precomputed (custom range, or a new quarter before tonight\'s run)'
  : reason === 'filter_not_supported' ? 'this filter is not supported (for an event, one with no linked workshop and no product tag cannot be matched to deals)'
    : reason === 'segment_not_supported' ? 'this audience is not computed'
    : reason === 'influence_reloading' ? 'the numbers are reloading, try again in a minute'
    : reason === 'program_x_segment_not_supported' ? 'a program filter cannot be combined with a segment'
    : reason === 'rows_use_previous_credit_rule' ? 'the stored rows predate the first-touch credit rule; tonight\'s run replaces them'
      : 'no influence scorecard rows for this view yet');

// Deep Dive breakdown rows (Product tab) from /exec/influence/tags: the shape the existing chart and table read.
export const BREAKDOWN_SEGMENT = { all: 'all', internal: 'internal-direct', external: 'customer', channel: 'partner' };

export function adaptTagRows(data) {
  if (!data?.available) return null;
  return (data.tags || []).map((t) => ({
    tag_id: t.tag_id,
    category: t.category,
    tag_name: t.name || `Tag ${t.tag_id}`,
    pipeline: t.value,
    won: t.won_value,
    win_rate: t.n_closed ? t.n_won / t.n_closed : null,
    win_rate_n: t.n_closed,
    n_won: t.n_won,
    open_share: t.n_deals ? t.n_open / t.n_deals : null,
    opp_count: t.n_deals,
    participant_count: null,
  }));
}

// "12 deals · win 50% (n=4, 43% open)": the Workshop Index cell of an influenced row.
export function influencedDealsText(w) {
  const win = w.win_rate_pct == null ? 'win rate —' : `win ${Math.round(w.win_rate_pct)}%`;
  const open = w.open_share_pct == null ? '' : `, ${Math.round(w.open_share_pct)}% open`;
  return `${(w.n_deals ?? 0).toLocaleString()} deals · ${win} (n=${(w.n_closed ?? 0).toLocaleString()}${open})`;
}

// Workshop Index with the cutover ON: money columns are the influenced rows (by workshop id), so the page sorts them itself.
export function sortWorkshopRows(rows, sort, influencedById) {
  const value = (row) => {
    if (sort.field === 'name') return (row.name || '').toLowerCase();
    if (sort.field === 'participant_count') return row.participant_count ?? 0;
    if (influencedById) {
      const infl = influencedById[String(row.id)];
      return infl ? (sort.field === 'won' ? infl.won : infl.pipeline) ?? 0 : -1;
    }
    return row[sort.field] ?? 0;
  };
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = value(a), y = value(b);
    return (x < y ? -1 : x > y ? 1 : 0) * dir;
  });
}
